/* NgRx */
import {createAction, props} from '@ngrx/store';
import {LoginModel} from "../models/login.model";
import {RegisterModel} from "../models/register.model";
import {ForgotPasswordModel} from "../models/forgot-password.model";
import {SetPasswordModel} from "../models/set-password.model";
import {GenerateOtpModel} from "../models/generate-otp.model";
import {VerifyOtpModel} from "../models/verify-otp.model";
import {VerifyUsernameModel} from "../models/verify-username.model";
import {ParticipantPhiAndPiiDetailsFormModel} from "../models/participant-phi-and-pii-details-form.model";
import {QuestionList} from "../../../models/question-list";
import {FetchObQuestionsModel} from "../models/fetch-ob-questions.model";
import {VerifyOtpForTfaModel} from "../models/verify-otp-for-tfa.model";
import {Filter} from "../../../models/filter";
import {FetchConsentFormModel} from '../models/fetch-consent-form.model';

/******** CLEAR STATE ***********/
const CLEAR_STATE = '[authentication] clear state';
export const ClearState = createAction(CLEAR_STATE);

/******** CLEAR PHONE NUMBER ***********/
const CLEAR_PHONE_NUMBER = '[authentication] clear phone number';
export const ClearPhoneNumber = createAction(CLEAR_PHONE_NUMBER);

/******** LOGIN ***********/
const LOGIN_REQUEST = '[authentication] login requested';
export const LoginRequest = createAction(LOGIN_REQUEST, props<{ payload: LoginModel['request'] }>());

const LOGIN_SUCCESS = '[authentication] login successful';
export const LoginSuccess = createAction(LOGIN_SUCCESS, props<{
  loginInfo: any,
  message: string
}>());

const LOGIN_FAILURE = '[authentication] login failure';
export const LoginFailure = createAction(LOGIN_FAILURE);

/******** LOGOUT ***********/
const LOGOUT_REQUEST = '[authentication] logout requested';
export const LogoutRequest = createAction(LOGOUT_REQUEST);

const LOGOUT_SUCCESS = '[authentication] logout successful';
export const LogoutSuccess = createAction(LOGOUT_SUCCESS, props<{ message: string }>());

const LOGOUT_FAILURE = '[authentication] logout failure';
export const LogoutFailure = createAction(LOGOUT_FAILURE, props<{ message: string }>());

/******** REGISTER ***********/
const REGISTER_REQUEST = '[authentication] register requested';
export const RegisterRequest = createAction(REGISTER_REQUEST, props<{
  payload: RegisterModel['request']
}>());

const REGISTER_SUCCESS = '[authentication] register successful';
export const RegisterSuccess = createAction(REGISTER_SUCCESS, props<{
  message: string
}>());

const REGISTER_FAILURE = '[authentication] register failure';
export const RegisterFailure = createAction(REGISTER_FAILURE);

/******** FORGOT PASSWORD ***********/
const FORGOT_PASSWORD_REQUEST = '[authentication] forgot-password requested';
export const ForgotPasswordRequest = createAction(FORGOT_PASSWORD_REQUEST, props<{
  payload: ForgotPasswordModel['request']
}>());

const FORGOT_PASSWORD_SUCCESS = '[authentication] forgot-password successful';
export const ForgotPasswordSuccess = createAction(FORGOT_PASSWORD_SUCCESS, props<{ message: string }>());

const FORGOT_PASSWORD_FAILURE = '[authentication] forgot-password failure';
export const ForgotPasswordFailure = createAction(FORGOT_PASSWORD_FAILURE);

/******** SET PASSWORD ***********/
const SET_PASSWORD_REQUEST = '[authentication] set-password password requested';
export const SetPasswordRequest = createAction(SET_PASSWORD_REQUEST, props<{
  payload: SetPasswordModel['request']
}>());

const SET_PASSWORD_SUCCESS = '[authentication] set-password password successful';
export const SetPasswordSuccess = createAction(SET_PASSWORD_SUCCESS, props<{ message: string }>());

const SET_PASSWORD_FAILURE = '[authentication] set-password password failure';
export const SetPasswordFailure = createAction(SET_PASSWORD_FAILURE, props<{ error: any }>());

/*********** GENERATE OTP **************/
const GENERATE_OTP_REQUEST = '[authentication] generate otp requested';
export const GenerateOtpRequest = createAction(GENERATE_OTP_REQUEST, props<{ payload: GenerateOtpModel['request'], resend?: boolean }>());

const GENERATE_OTP_SUCCESS = '[authentication] generate otp successful';
export const GenerateOtpSuccess = createAction(GENERATE_OTP_SUCCESS, props<{ message: string, phoneNumber: string }>());

const GENERATE_OTP_FAILURE = '[authentication] generate otp failure';
export const GenerateOtpFailure = createAction(GENERATE_OTP_FAILURE, props<{ message: string }>());

/*********** VERIFY OTP **************/
const VERIFY_OTP_REQUEST = '[authentication] verify otp requested';
export const VerifyOtpRequest = createAction(VERIFY_OTP_REQUEST, props<{ payload: VerifyOtpModel['request'] }>());

const VERIFY_OTP_SUCCESS = '[authentication] verify otp successful';
export const VerifyOtpSuccess = createAction(VERIFY_OTP_SUCCESS, props<{ message: string }>());

const VERIFY_OTP_FAILURE = '[authentication] verify otp failure';
export const VerifyOtpFailure = createAction(VERIFY_OTP_FAILURE, props<{ message: string }>());

/*********** SET HAS MEDICATION STATUS **************/
const SET_HAS_MEDICATION_STATUS = '[authentication] set has medication status';
export const SetHasMedicationStatus = createAction(SET_HAS_MEDICATION_STATUS, props<{ hasMedication: boolean }>());

/*********** SET HAS MEDICAL PROBLEMS STATUS **************/
const SET_HAS_MEDICAL_PROBLEMS_STATUS = '[authentication] set has medical problems status';
export const SetHasMedicalProblemStatus = createAction(SET_HAS_MEDICAL_PROBLEMS_STATUS, props<{
  hasMedicalProblems: boolean
}>());

/*********** FETCH CONSENT FORM LINK **************/
const FETCH_CONSENT_FORM_LINK_REQUEST = '[authentication] fetch consent form link requested';
export const FetchConsentFormLinkRequest = createAction(FETCH_CONSENT_FORM_LINK_REQUEST, props<{payload?: FetchConsentFormModel['request']}>());

const FETCH_CONSENT_FORM_LINK_SUCCESS = '[authentication] fetch consent form link successful';
export const FetchConsentFormLinkSuccess = createAction(FETCH_CONSENT_FORM_LINK_SUCCESS, props<{
  consentFormLink: string,
  message: string
}>());

const FETCH_CONSENT_FORM_LINK_FAILURE = '[authentication] fetch consent form link failure';
export const FetchConsentFormLinkFailure = createAction(FETCH_CONSENT_FORM_LINK_FAILURE, props<{ message: string }>());

/*********** VERIFY USERNAME **************/
const VERIFY_USERNAME_REQUEST = '[authentication] verify username requested';
export const VerifyUsernameRequest = createAction(VERIFY_USERNAME_REQUEST, props<{
  payload: VerifyUsernameModel['request']
}>());

const VERIFY_USERNAME_SUCCESS = '[authentication] verify username successful';
export const VerifyUsernameSuccess = createAction(VERIFY_USERNAME_SUCCESS, props<{
  message: string,
  isAvailable: boolean
}>());

const VERIFY_USERNAME_FAILURE = '[authentication] verify username failure';
export const VerifyUsernameFailure = createAction(VERIFY_USERNAME_FAILURE, props<{ message: string }>());

/*********** FETCH CONSENT FORM STATUS **************/
const FETCH_CONSENT_STATUS_REQUEST = '[authentication] fetch consent status requested';
export const FetchConsentStatusRequest = createAction(FETCH_CONSENT_STATUS_REQUEST);

const FETCH_CONSENT_STATUS_SUCCESS = '[authentication] fetch consent status successful';
export const FetchConsentStatusSuccess = createAction(FETCH_CONSENT_STATUS_SUCCESS, props<{
  message: string
}>());

const FETCH_CONSENT_STATUS_FAILURE = '[authentication] fetch consent status failure';
export const FetchConsentStatusFailure = createAction(FETCH_CONSENT_STATUS_FAILURE, props<{ message: string }>());

/*********** SET ROLE **************/
const SET_ROLE = '[authentication] set role';
export const SetRole = createAction(SET_ROLE, props<{ role: number }>());

/*********** FETCH OB QUESTIONS **************/
const FETCH_OB_QUESTIONS_REQUEST = '[authentication] fetch ob questions requested';
export const FetchObQuestionsRequest = createAction(FETCH_OB_QUESTIONS_REQUEST, props<{
  payload: FetchObQuestionsModel['request']
}>());

const FETCH_OB_QUESTIONS_SUCCESS = '[authentication] fetch ob questions successful';
export const FetchObQuestionsSuccess = createAction(FETCH_OB_QUESTIONS_SUCCESS, props<{
  questionList: QuestionList,
  message: string
}>());

const FETCH_OB_QUESTIONS_FAILURE = '[authentication] fetch ob questions failure';
export const FetchObQuestionsFailure = createAction(FETCH_OB_QUESTIONS_FAILURE, props<{
  message: string
}>());

/*********** START REGISTRATION LOADING **************/
const START_REGISTRATION_LOADING = '[authentication] start registration loading';
export const StartRegistrationLoading = createAction(START_REGISTRATION_LOADING);

/*********** STOP REGISTRATION LOADING **************/
const STOP_REGISTRATION_LOADING = '[authentication] stop registration loading';
export const StopRegistrationLoading = createAction(STOP_REGISTRATION_LOADING);

/*********** SAVE ANSWERS **************/
const SAVE_ANSWERS_REQUEST = '[authentication] save answers requested';
export const SaveAnswersRequest = createAction(SAVE_ANSWERS_REQUEST, props<any>());

const SAVE_ANSWERS_SUCCESS = '[authentication] save answers successful';
export const SaveAnswersSuccess = createAction(SAVE_ANSWERS_SUCCESS, props<any>());

const SAVE_ANSWERS_FAILURE = '[authentication] save answers failure';
export const SaveAnswersFailure = createAction(SAVE_ANSWERS_FAILURE);

/*********** VERIFY OTP FOR TFA **************/
const VERIFY_OTP_FOR_TFA_REQUEST = '[authentication] verify otp for tfa requested';
export const VerifyOtpForTfaRequest = createAction(VERIFY_OTP_FOR_TFA_REQUEST, props<{
  payload: VerifyOtpForTfaModel['request']
}>());

const VERIFY_OTP_FOR_TFA_SUCCESS = '[authentication] verify otp for tfa successful';
export const VerifyOtpForTfaSuccess = createAction(VERIFY_OTP_FOR_TFA_SUCCESS, props<{
  verifyOtpInfo: any,
  message: string
}>());

const VERIFY_OTP_FOR_TFA_FAILURE = '[authentication] verify otp for tfa failure';
export const VerifyOtpForTfaFailure = createAction(VERIFY_OTP_FOR_TFA_FAILURE, props<{ message: string }>());
